import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Volume2, CheckCircle, 
  Award, Keyboard, RefreshCw, Zap, Target,
  ArrowLeft, Home, Play, Pause, HelpCircle, BookOpen, PenTool
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { WritingCanvas, isWritingCourse } from '../components/WritingCanvas';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AlphabetLesson = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  
  const [courseData, setCourseData] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('learn'); // 'learn', 'practice', 'results'
  
  // Lesson-specific data (example words with audio)
  const [lessonData, setLessonData] = useState(null);
  const [playingWord, setPlayingWord] = useState(null);
  const audioRef = useRef(null);
  
  // Practice state
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showHint, setShowHint] = useState(false);
  
  const inputRef = useRef(null);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  // Fetch lesson-specific data when lesson index changes
  useEffect(() => {
    if (courseData?.lessons?.[currentLessonIndex]) {
      fetchLessonData(courseData.lessons[currentLessonIndex].lesson_id);
    }
  }, [courseData, currentLessonIndex]);

  const fetchCourseData = async () => {
    try {
      const response = await api.get(`/alphabet/course/${courseId}`);
      setCourseData(response.data);
    } catch (err) {
      console.error('Failed to fetch course:', err);
      toast.error(t("alphabetLesson.loadCourseError"));
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonData = async (lessonId) => {
    try {
      const response = await api.get(`/alphabet/lesson/${courseId}/${lessonId}`);
      setLessonData(response.data);
    } catch (err) {
      console.error('Failed to fetch lesson data:', err);
    }
  };

  // Map course IDs to TTS-compatible language codes
  const getTTSLanguageCode = (course) => {
    const ttsLanguageMap = {
      'hebrew_alphabet': 'he',    // Modern Hebrew TTS (gTTS supports 'he')
      'greek_alphabet': 'el',     // Modern Greek TTS (gTTS supports 'el')
      'syriac_alphabet': 'ar',    // Use Arabic TTS as closest available
      'tibetan_alphabet': null,   // No TTS available for Tibetan
      'russian_alphabet': 'ru',   // Russian TTS
      'korean_alphabet': 'ko',    // Korean TTS
      'arabic_alphabet': 'ar',    // Arabic TTS
      'hindi_devanagari': 'hi',   // Hindi TTS
    };
    return ttsLanguageMap[course] || null;
  };

  // Play audio for a word (database words with audio files)
  const playWordAudio = async (word) => {
    if (playingWord === word) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setPlayingWord(word);
    
    try {
      const languageCode = courseId === 'hebrew_alphabet' ? 'hbo' : 'grc';
      const audioUrl = `${BACKEND_URL}/api/vocabulary/word/${encodeURIComponent(word)}/audio?language_code=${languageCode}`;
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingWord(null);
      };
      
      audio.onerror = () => {
        setPlayingWord(null);
        toast.error(t('alphabetLesson.audioError'));
      };
      
      await audio.play();
    } catch (err) {
      console.error('Audio error:', err);
      setPlayingWord(null);
      toast.error(t('alphabetLesson.audioError'));
    }
  };

  // Play TTS audio for hardcoded example words
  const playExampleAudio = async (word) => {
    if (playingWord === word) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    const ttsLang = getTTSLanguageCode(courseId);
    if (!ttsLang) {
      toast.error(t('alphabetLesson.audioUnavailable'));
      return;
    }
    
    setPlayingWord(word);
    
    try {
      // Use the TTS endpoint to generate audio on-the-fly
      const audioUrl = `${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(word)}&lang=${ttsLang}`;
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingWord(null);
      };
      
      audio.onerror = () => {
        setPlayingWord(null);
        // Silently fail - TTS might not work for all words
        console.log('TTS audio failed for:', word);
      };
      
      await audio.play();
    } catch (err) {
      console.error('TTS audio error:', err);
      setPlayingWord(null);
    }
  };

  // Play audio for a letter name
  const [playingLetter, setPlayingLetter] = useState(false);
  
  const playLetterAudio = async (letterName) => {
    if (playingLetter) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setPlayingLetter(true);
    
    try {
      const audioUrl = `${BACKEND_URL}/api/alphabet/letter-audio/${courseId}/${encodeURIComponent(letterName)}`;
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingLetter(false);
      };
      
      audio.onerror = () => {
        setPlayingLetter(false);
        toast.error(t('alphabetLesson.letterAudioError'));
      };
      
      await audio.play();
    } catch (err) {
      console.error('Letter audio error:', err);
      setPlayingLetter(false);
      toast.error(t('alphabetLesson.letterAudioError'));
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const fetchPracticeQuestions = async () => {
    try {
      const response = await api.get(`/alphabet/practice/${courseId}?count=10`);
      setQuestions(response.data.questions || []);
      setCurrentQuestionIndex(0);
      setScore(0);
      setStreak(0);
      setMode('practice');
    } catch (err) {
      console.error('Failed to fetch practice questions:', err);
      toast.error(t("alphabetLesson.loadQuestionsError"));
    }
  };

  const currentLesson = courseData?.lessons?.[currentLessonIndex];
  const currentQuestion = questions[currentQuestionIndex];
  const isRTL = ['hebrew_alphabet', 'arabic_alphabet', 'syriac_alphabet'].includes(courseId);

  const handleNextLesson = () => {
    if (currentLessonIndex < (courseData?.lessons?.length || 0) - 1) {
      setCurrentLessonIndex(prev => prev + 1);
    }
  };

  const handlePrevLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(prev => prev - 1);
    }
  };

  const handleAnswerSubmit = useCallback(() => {
    if (!currentQuestion || !userAnswer.trim()) return;
    
    const acceptableAnswers = currentQuestion.accept_answers || [currentQuestion.correct_answer];
    const isAnswerCorrect = acceptableAnswers.some(
      ans => ans.toLowerCase().trim() === userAnswer.toLowerCase().trim()
    );
    
    setIsCorrect(isAnswerCorrect);
    setShowResult(true);
    
    if (isAnswerCorrect) {
      setScore(prev => prev + 1);
      setStreak(prev => prev + 1);
    } else {
      setStreak(0);
    }
  }, [currentQuestion, userAnswer]);

  const handleNextQuestion = () => {
    setShowResult(false);
    setUserAnswer('');
    setShowHint(false);
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      // Show results
      setMode('results');
      // Save progress
      saveProgress();
    }
  };

  const saveProgress = async () => {
    try {
      await api.post(`/alphabet/progress/${courseId}`, {
        lesson_id: currentLesson?.lesson_id,
        completed: true,
        score: Math.round((score / questions.length) * 100)
      });
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !showResult) {
      handleAnswerSubmit();
    } else if (e.key === 'Enter' && showResult) {
      handleNextQuestion();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  // Results Screen
  if (mode === 'results') {
    const percentage = Math.round((score / questions.length) * 100);
    const isPassing = percentage >= 70;
    
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 text-center">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
            isPassing ? 'bg-green-500/20' : 'bg-amber-500/20'
          }`}>
            {isPassing ? (
              <Award className="w-10 h-10 text-green-400" />
            ) : (
              <Target className="w-10 h-10 text-amber-400" />
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">
            {isPassing ? 'Excellent Work!' : 'Keep Practicing!'}
          </h2>
          
          <p className="text-slate-400 mb-6">
            You scored {score} out of {questions.length} ({percentage}%)
          </p>
          
          {/* Score breakdown */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-400">{score}</div>
              <div className="text-sm text-slate-400">{t('alphabetLesson.correctCount')}</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-400">{questions.length - score}</div>
              <div className="text-sm text-slate-400">{t('alphabetLesson.incorrectCount')}</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-amber-400">{percentage}%</div>
              <div className="text-sm text-slate-400">Score</div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setMode('learn');
                setScore(0);
                setStreak(0);
              }}
              className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
            >
              <span>{t('alphabetLesson.reviewLetters')}</span>
            </button>
            <button
              onClick={fetchPracticeQuestions}
              className="px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold transition-colors"
            >
              <span>{t('alphabetLesson.practiceAgain')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Practice Mode
  if (mode === 'practice' && currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">Question {currentQuestionIndex + 1} of {questions.length}</span>
            <div className="flex items-center gap-4">
              <span className="text-green-400">Score: {score}</span>
              {streak > 1 && (
                <span className="flex items-center gap-1 text-amber-400">
                  <Zap className="w-4 h-4" />
                  {streak} streak!
                </span>
              )}
            </div>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
          <h3 className="text-xl text-slate-300 mb-6 text-center">{currentQuestion.question}</h3>
          
          {/* Letter Display */}
          {currentQuestion.display && (
            <div 
              className="text-center mb-8"
              style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            >
              <span className={`inline-block px-8 py-4 rounded-xl bg-slate-700/50 border-2 border-slate-600 ${
                currentQuestion.display_size === 'large' ? 'text-8xl' : 'text-4xl'
              }`}>
                {currentQuestion.display}
              </span>
            </div>
          )}

          {/* Multiple Choice Options */}
          {currentQuestion.type === 'multiple_choice' && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    setUserAnswer(option.id);
                    setIsCorrect(option.id === currentQuestion.correct_answer);
                    setShowResult(true);
                    if (option.id === currentQuestion.correct_answer) {
                      setScore(prev => prev + 1);
                      setStreak(prev => prev + 1);
                    } else {
                      setStreak(0);
                    }
                  }}
                  disabled={showResult}
                  className={`p-6 rounded-xl border-2 transition-all text-4xl ${
                    showResult
                      ? option.id === currentQuestion.correct_answer
                        ? 'border-green-500 bg-green-500/20'
                        : option.id === userAnswer
                          ? 'border-red-500 bg-red-500/20'
                          : 'border-slate-600 opacity-50'
                      : 'border-slate-600 hover:border-amber-500 bg-slate-700/50'
                  }`}
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                >
                  {option.display}
                </button>
              ))}
            </div>
          )}

          {/* Type Answer */}
          {currentQuestion.type === 'type_answer' && (
            <div className="mb-6">
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={showResult}
                  placeholder={t('alphabetLesson.typeYourAnswer')}
                  autoFocus
                  className={`w-full px-4 py-3 rounded-lg bg-slate-700 border-2 text-white text-xl text-center placeholder-slate-500 focus:outline-none transition-colors ${
                    showResult
                      ? isCorrect 
                        ? 'border-green-500' 
                        : 'border-red-500'
                      : 'border-slate-600 focus:border-amber-500'
                  }`}
                  data-testid="answer-input"
                />
                <Keyboard className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              </div>
              
              {/* Hint Button */}
              {!showResult && currentQuestion.hint && (
                <button
                  onClick={() => setShowHint(!showHint)}
                  className="mt-3 text-sm text-slate-400 hover:text-amber-400 flex items-center gap-1"
                >
                  <HelpCircle className="w-4 h-4" />
                  {showHint ? 'Hide hint' : 'Show hint'}
                </button>
              )}
              
              {showHint && (
                <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm">
                  {currentQuestion.hint}
                </div>
              )}
              
              {!showResult && (
                <button
                  onClick={handleAnswerSubmit}
                  disabled={!userAnswer.trim()}
                  className="w-full mt-4 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-semibold transition-colors"
                  data-testid="submit-answer"
                >
                  Check Answer
                </button>
              )}
            </div>
          )}

          {/* Result Feedback */}
          {showResult && (
            <div className={`p-4 rounded-lg mb-6 ${isCorrect ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
              <div className="flex items-center gap-2 mb-2">
                {isCorrect ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-semibold">{t('alphabetLesson.correct')}</span>
                  </>
                ) : (
                  <>
                    <span className="text-red-400 font-semibold">{t('alphabetLesson.notQuite')}</span>
                  </>
                )}
              </div>
              {!isCorrect && (
                <p className="text-slate-300 text-sm">
                  The correct answer was: <span className="text-amber-400 font-semibold">{currentQuestion.correct_answer}</span>
                </p>
              )}
            </div>
          )}

          {/* Next Button */}
          {showResult && (
            <button
              onClick={handleNextQuestion}
              className="w-full px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold transition-colors"
              data-testid="next-question"
            >
              {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'See Results'}
            </button>
          )}
        </div>

        {/* Back to Learn */}
        <button
          onClick={() => setMode('learn')}
          className="mt-4 text-slate-400 hover:text-white flex items-center gap-2 mx-auto"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to lessons
        </button>
      </div>
    );
  }

  // Write Mode (Tracing/Freehand)
  if (mode === 'write' && currentLesson) {
    const letterChar = currentLesson.letter || currentLesson.lowercase || '';
    // For Arabic, use isolated form if available
    const writeLetter = currentLesson.forms?.isolated || letterChar;

    const handleScoreSubmit = async ({ mode: wMode, score: wScore }) => {
      try {
        await api.post(`/alphabet/writing-progress/${courseId}`, {
          lesson_id: currentLesson.lesson_id,
          mode: wMode,
          score: wScore,
          letter: writeLetter
        });
      } catch (err) {
        // Silently fail - progress saving is non-critical
        console.log('Writing progress save failed:', err);
      }
    };

    return (
      <div className="max-w-2xl mx-auto">
        {/* Header with nav */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setMode('learn')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('alphabetLesson.backToLesson')}</span>
          </button>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <PenTool className="w-5 h-5 text-emerald-400" />
            Writing Practice
          </h2>
          <div className="w-24" />
        </div>

        {/* Current letter info */}
        <div className="flex items-center justify-between mb-4 px-2">
          <button
            onClick={() => { if (currentLessonIndex > 0) setCurrentLessonIndex(prev => prev - 1); }}
            disabled={currentLessonIndex === 0}
            className="p-2 rounded-lg bg-slate-700 text-slate-300 disabled:opacity-30 hover:bg-slate-600 transition-colors"
            data-testid="write-prev-letter"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <span className="text-4xl font-bold text-white" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
              {writeLetter}
            </span>
            <p className="text-sm text-slate-400 mt-1">{currentLesson.name} — {currentLesson.transliteration}</p>
          </div>
          <button
            onClick={() => { if (currentLessonIndex < (courseData?.lessons?.length || 0) - 1) setCurrentLessonIndex(prev => prev + 1); }}
            disabled={currentLessonIndex === (courseData?.lessons?.length || 0) - 1}
            className="p-2 rounded-lg bg-slate-700 text-slate-300 disabled:opacity-30 hover:bg-slate-600 transition-colors"
            data-testid="write-next-letter"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Writing Canvas */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
          <WritingCanvas
            letter={writeLetter}
            letterName={currentLesson.name}
            courseId={courseId}
            isRTL={isRTL}
            onScoreSubmit={handleScoreSubmit}
          />
        </div>

        {/* Quick letter selector */}
        <div className="mt-6">
          <p className="text-sm text-slate-500 mb-3 text-center">{t('alphabetLesson.jumpToLetter')}</p>
          <div className="flex flex-wrap gap-1.5 justify-center">
            {courseData?.lessons?.filter(l => l.module === 'consonants' || l.module === 'letters' || l.module === 'vowels').slice(0, 30).map((lesson) => {
              const actualIndex = courseData.lessons.findIndex(ll => ll.lesson_id === lesson.lesson_id);
              const lChar = lesson.forms?.isolated || lesson.letter || lesson.lowercase;
              return (
                <button
                  key={lesson.lesson_id}
                  onClick={() => setCurrentLessonIndex(actualIndex)}
                  className={`w-9 h-9 rounded-lg border text-lg transition-all ${
                    actualIndex === currentLessonIndex
                      ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                      : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-500'
                  }`}
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  title={lesson.name}
                >
                  {lChar}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Learn Mode
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link 
          to="/alphabet-courses"
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('alphabetLesson.backToCourses')}</span>
        </Link>
        <h1 className="text-xl font-bold text-white">{courseData?.name}</h1>
        <div className="flex items-center gap-3">
          <Link
            to={`/grammar-library?alphabet_course=${courseId}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
            data-testid="alphabet-overview-link"
            title="View writing system guide"
          >
            <BookOpen className="w-5 h-5 text-amber-400" />
            <span className="hidden sm:inline">{t('alphabetLesson.overview')}</span>
          </Link>
          {isWritingCourse(courseId) && (
            <button
              onClick={() => setMode('write')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                mode === 'write'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-400 border border-emerald-500/30'
              }`}
              data-testid="start-writing"
            >
              <PenTool className="w-5 h-5" />
              <span>Write</span>
            </button>
          )}
          <button
            onClick={fetchPracticeQuestions}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold transition-colors"
            data-testid="start-practice"
          >
            <Target className="w-5 h-5" />
            <span>{t('alphabetLesson.practice')}</span>
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-slate-400">Lesson {currentLessonIndex + 1} of {courseData?.lessons?.length}</span>
          <span className="text-amber-400">{currentLesson?.module}</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
            style={{ width: `${((currentLessonIndex + 1) / (courseData?.lessons?.length || 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Lesson Card */}
      {currentLesson && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
          {/* Letter Display */}
          <div className="text-center mb-8">
            <div 
              className="inline-block px-12 py-8 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-800 border-2 border-slate-600 shadow-xl relative"
              style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            >
              <span className="text-9xl font-bold text-amber-400">
                {currentLesson.letter || currentLesson.lowercase}
              </span>
              {currentLesson.lowercase && currentLesson.letter !== currentLesson.lowercase && (
                <span className="text-6xl text-slate-400 ml-4">{currentLesson.lowercase}</span>
              )}
              {currentLesson.final_form && (
                <span className="text-6xl text-slate-500 ml-4">({currentLesson.final_form})</span>
              )}
              
              {/* Letter Audio Button */}
              <button
                onClick={() => playLetterAudio(currentLesson.name)}
                disabled={playingLetter}
                className={`absolute -bottom-4 left-1/2 transform -translate-x-1/2 p-3 rounded-full shadow-lg transition-all ${
                  playingLetter
                    ? 'bg-amber-500 text-white scale-110'
                    : 'bg-amber-500/90 hover:bg-amber-500 text-white hover:scale-105'
                }`}
                data-testid="play-letter-audio"
              >
                <Volume2 className={`w-6 h-6 ${playingLetter ? 'animate-pulse' : ''}`} />
              </button>
            </div>
            
            {/* Tap to hear hint */}
            <p className="text-sm text-slate-500 mt-6">{t('alphabetLesson.tapSpeakerHint')}</p>
          </div>

          {/* All 4 Forms Display */}
          {currentLesson.forms && (
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 text-center">{t('alphabetLesson.letterForms')}</h3>
              <div className="grid grid-cols-4 gap-3">
                {['isolated', 'initial', 'medial', 'final'].map(form => (
                  <div key={form} className="text-center p-4 rounded-xl bg-slate-700/50 border border-slate-600/50">
                    <p className="text-xs text-slate-500 uppercase mb-2">{form}</p>
                    <span className="text-5xl text-amber-300" style={{ direction: 'rtl', fontFamily: 'serif' }}>
                      {currentLesson.forms[form] || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Letter Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                <span className="text-slate-400">Name</span>
                <span className="text-xl font-semibold text-white">{currentLesson.name}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                <span className="text-slate-400">{t('alphabetLesson.transliteration')}</span>
                <span className="text-xl font-mono text-amber-400">{currentLesson.transliteration}</span>
              </div>
              {currentLesson.numeric_value && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                  <span className="text-slate-400">{t('alphabetLesson.numericValue')}</span>
                  <span className="text-xl text-blue-400">{currentLesson.numeric_value}</span>
                </div>
              )}
              {currentLesson.matra !== undefined && currentLesson.matra !== "" && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                  <span className="text-slate-400">{t('alphabetLesson.matra')}</span>
                  <span className="text-3xl text-emerald-400">क{currentLesson.matra}</span>
                </div>
              )}
              {currentLesson.group && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                  <span className="text-slate-400">Group</span>
                  <span className="text-sm font-medium text-purple-400 capitalize">{currentLesson.group}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-slate-700/50">
                <div className="text-slate-400 text-sm mb-2">{t('alphabetLesson.pronunciation')}</div>
                <div className="flex items-center gap-3">
                  <p className="text-lg text-white">{currentLesson.pronunciation}</p>
                  <button 
                    onClick={() => playLetterAudio(currentLesson.name)}
                    disabled={playingLetter}
                    className={`p-2 rounded-full transition-colors ${
                      playingLetter
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400'
                    }`}
                    data-testid="play-letter-pronunciation"
                  >
                    <Volume2 className={`w-5 h-5 ${playingLetter ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-slate-700/50">
                <div className="text-slate-400 text-sm mb-2">Type</div>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  currentLesson.type === 'consonant' ? 'bg-blue-500/20 text-blue-400' :
                  currentLesson.type === 'vowel' ? 'bg-green-500/20 text-green-400' :
                  currentLesson.type === 'sign' ? 'bg-purple-500/20 text-purple-400' :
                  currentLesson.type?.includes('vowel') ? 'bg-purple-500/20 text-purple-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>
                  {currentLesson.type?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Cognates Section for Russian */}
          {currentLesson.cognates && currentLesson.cognates.length > 0 && (
            <div className="mb-8 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-semibold text-blue-400">
                  English Cognates
                </h3>
                <span className="text-xs text-blue-400/70 ml-auto flex items-center gap-1">
                  <Volume2 className="w-3 h-3" /> Audio available
                </span>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Words similar to English containing the letter <span className="text-white font-bold">{currentLesson.letter?.split(' ')[0]}</span>. 
                Click the speaker to hear pronunciation!
              </p>
              <div className="grid gap-3">
                {currentLesson.cognates.map((cognate, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:bg-slate-800/70 transition-colors"
                  >
                    {/* Audio button for Russian */}
                    <button
                      onClick={() => playExampleAudio(cognate.word)}
                      disabled={playingWord === cognate.word}
                      className={`p-2 rounded-lg transition-all ${
                        playingWord === cognate.word 
                          ? 'bg-blue-500/30 text-blue-400 animate-pulse' 
                          : 'bg-slate-700 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400'
                      }`}
                      title="Play pronunciation"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                    
                    {/* Russian word */}
                    <div className="flex flex-col min-w-[140px]">
                      <span className="text-2xl font-bold text-white">
                        {cognate.word}
                      </span>
                      <span className="text-sm text-amber-400 font-mono">
                        {cognate.transliteration}
                      </span>
                    </div>
                    
                    {/* Arrow */}
                    <span className="text-slate-500">→</span>
                    
                    {/* English meaning */}
                    <span className="text-slate-300 flex-1">
                      {cognate.meaning}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hardcoded Examples Section for Biblical/Modern Languages */}
          {currentLesson.examples && currentLesson.examples.length > 0 && (
            <div className="mb-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-semibold text-emerald-400">
                  {courseId === 'hebrew_alphabet' ? 'Biblical Hebrew Words' :
                   courseId === 'greek_alphabet' ? 'New Testament Greek Words' :
                   courseId === 'syriac_alphabet' ? 'Peshitta Vocabulary' :
                   courseId === 'tibetan_alphabet' ? 'Buddhist Terminology' :
                   courseId === 'hindi_devanagari' ? 'Hindi Words' :
                   'Example Words'}
                </h3>
                {/* Audio availability indicator */}
                {getTTSLanguageCode(courseId) && (
                  <span className="text-xs text-emerald-400/70 ml-auto flex items-center gap-1">
                    <Volume2 className="w-3 h-3" /> Audio available
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-400 mb-4">
                {courseId === 'hebrew_alphabet' ? 'Common Biblical Hebrew words starting with this letter. Click the speaker to hear pronunciation.' :
                 courseId === 'greek_alphabet' ? 'Frequently used words from the Greek New Testament. Click the speaker to hear pronunciation.' :
                 courseId === 'syriac_alphabet' ? 'Key vocabulary from the Syriac Peshitta.' :
                 courseId === 'tibetan_alphabet' ? 'Essential Buddhist terminology using this letter.' :
                 courseId === 'hindi_devanagari' ? 'Common Hindi words using this letter. Click the speaker to hear pronunciation.' :
                 'Example words using this letter.'}
              </p>
              <div className="grid gap-3">
                {currentLesson.examples.map((example, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:bg-slate-800/70 transition-colors"
                  >
                    {/* Audio button - only show if TTS is available */}
                    {getTTSLanguageCode(courseId) && (
                      <button
                        onClick={() => playExampleAudio(example.word)}
                        disabled={playingWord === example.word}
                        className={`p-2 rounded-lg transition-all ${
                          playingWord === example.word 
                            ? 'bg-emerald-500/30 text-emerald-400 animate-pulse' 
                            : 'bg-slate-700 text-slate-400 hover:bg-emerald-500/20 hover:text-emerald-400'
                        }`}
                        title="Play pronunciation"
                      >
                        <Volume2 className="w-4 h-4" />
                      </button>
                    )}
                    
                    {/* Word in original script */}
                    <div className="flex flex-col min-w-[140px]">
                      <span className={`font-bold text-white ${
                        courseId === 'hebrew_alphabet' || courseId === 'syriac_alphabet' ? 'text-3xl' : 'text-2xl'
                      }`}>
                        {example.word}
                      </span>
                      <span className="text-sm text-amber-400 font-mono">
                        {example.transliteration}
                      </span>
                    </div>
                    
                    {/* Arrow */}
                    <span className="text-slate-500">→</span>
                    
                    {/* English meaning */}
                    <span className="text-slate-300 flex-1">
                      {example.meaning}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Example Words from Database (fallback) */}
          {lessonData?.example_words?.length > 0 && (!currentLesson.examples || currentLesson.examples.length === 0) && (
            <div className="mb-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-semibold text-emerald-400">
                  Example Words from the {
                    courseId === 'syriac_alphabet' ? 'Peshitta' : 
                    courseId === 'tibetan_alphabet' ? 'Tibetan Texts' :
                    courseId === 'russian_alphabet' ? 'Russian Vocabulary' :
                    'Bible'
                  }
                </h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Words containing this letter. {(courseId === 'syriac_alphabet' || courseId === 'tibetan_alphabet') && 'Transliterations shown for each word. '}Click the speaker icon to hear pronunciation.
              </p>
              <div className="grid gap-3">
                {lessonData.example_words.map((exWord, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700"
                  >
                    {/* Audio Button - only enabled if has_audio is true */}
                    {exWord.has_audio ? (
                      <button
                        onClick={() => playWordAudio(exWord.word)}
                        disabled={playingWord === exWord.word}
                        className={`flex-shrink-0 p-2 rounded-full transition-colors ${
                          playingWord === exWord.word
                            ? 'bg-emerald-500 text-white'
                            : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400'
                        }`}
                        data-testid={`play-word-${idx}`}
                      >
                        <Volume2 className={`w-5 h-5 ${playingWord === exWord.word ? 'animate-pulse' : ''}`} />
                      </button>
                    ) : (
                      <div 
                        className="flex-shrink-0 p-2 rounded-full bg-slate-700/50 text-slate-500 cursor-not-allowed"
                        title="Audio not available"
                      >
                        <Volume2 className="w-5 h-5" />
                      </div>
                    )}
                    
                    {/* Word with optional transliteration */}
                    <div className="flex flex-col min-w-[120px]">
                      <span 
                        className="text-2xl font-bold text-white"
                        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                      >
                        {exWord.word}
                      </span>
                      {/* Show transliteration for Syriac or if available */}
                      {exWord.transliteration && (
                        <span className="text-sm text-amber-400 font-mono">
                          {exWord.transliteration}
                        </span>
                      )}
                    </div>
                    
                    {/* Definition */}
                    {exWord.definition && (
                      <span className="text-sm text-slate-400 flex-1">
                        {exWord.definition}
                      </span>
                    )}
                    
                    {/* Frequency Badge */}
                    {exWord.count > 0 && (
                      <span className="flex-shrink-0 text-xs px-2 py-1 rounded-full bg-slate-700 text-slate-300">
                        ×{exWord.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevLesson}
              disabled={currentLessonIndex === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
              data-testid="prev-lesson"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>{t('alphabetLesson.previous')}</span>
            </button>
            
            <div className="flex gap-1">
              {courseData?.lessons?.slice(
                Math.max(0, currentLessonIndex - 4),
                Math.min(courseData.lessons.length, currentLessonIndex + 5)
              ).map((lesson, idx) => {
                const actualIndex = Math.max(0, currentLessonIndex - 4) + idx;
                return (
                  <button
                    key={lesson.lesson_id}
                    onClick={() => setCurrentLessonIndex(actualIndex)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      actualIndex === currentLessonIndex
                        ? 'bg-amber-500'
                        : 'bg-slate-600 hover:bg-slate-500'
                    }`}
                  />
                );
              })}
            </div>
            
            <button
              onClick={handleNextLesson}
              disabled={currentLessonIndex === (courseData?.lessons?.length || 0) - 1}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
              data-testid="next-lesson"
            >
              <span>Next</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Quick Reference */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-white mb-4">{t('alphabetLesson.quickReference')}</h3>
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-11 gap-2">
          {courseData?.lessons?.filter(l => l.module === 'consonants' || l.module === 'letters' || l.module === 'vowels').map((lesson) => {
            const actualIndex = courseData.lessons.findIndex(ll => ll.lesson_id === lesson.lesson_id);
            return (
              <button
                key={lesson.lesson_id}
                onClick={() => setCurrentLessonIndex(actualIndex)}
                className={`p-2 rounded-lg border transition-all text-2xl ${
                  actualIndex === currentLessonIndex
                    ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                    : 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500'
                }`}
                style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                title={lesson.name}
              >
                {lesson.letter || lesson.lowercase}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AlphabetLesson;
